import {AnchoredOverlay, Button, Link, Stack, Text} from '@primer/react'
import React, {useState} from 'react'
import type {Author, CommitAuthor} from '../../types/blackbird-types'
import {GitHubAvatar} from '@github-ui/github-avatar'
import styles from './CommitAuthors.module.css'

/*
 * This component is responsible for rendering the commit authors, logic the same as attribution_info_component.html.erb
 */
export function CommitAuthors({
  commitAuthors,
  committerAttribution,
  repositoryUrl,
  committer,
  authorTooltip,
  commitId,
}: {
  commitAuthors: CommitAuthor[]
  committerAttribution: boolean
  repositoryUrl: string
  committer: Author
  authorTooltip: string
  commitId: string
}) {
  const [authorsOverlayOpen, setAuthorsOverlayOpen] = useState(false)

  const getCommitAuthorsList = () => {
    const commitAuthorsList = []
    for (const author of commitAuthors) {
      const authorUrl = author.login ? `${repositoryUrl}/commits?author=${author.login}` : undefined
      const authorName = author.login ?? author.displayName
      const lastIndex = commitAuthors.findIndex(a => a === author) === commitAuthors.length - 1

      commitAuthorsList.push(
        <React.Fragment key={`${commitId}-${authorName}`}>
          <GitHubAvatar size={16} src={author.developmentAvatar || author.avatarUrl} className={styles.authorAvatar} />
          &nbsp;
          {authorUrl ? (
            <Link href={`/${authorUrl}`}>
              <span className={styles.authorName}>{authorName}&nbsp;</span>
            </Link>
          ) : (
            <span className={styles.authorName}>{authorName}&nbsp;</span>
          )}
          {!lastIndex && <>and&nbsp;</>}
        </React.Fragment>,
      )
    }
    return commitAuthorsList
  }

  const committerUrl = committer.login ? `${repositoryUrl}/commits?author=${committer.login}` : undefined

  return (
    <div className={styles.commitAuthors}>
      {commitAuthors.length === 1 || (commitAuthors.length === 2 && !committerAttribution) ? (
        getCommitAuthorsList()
      ) : (
        <AnchoredOverlay
          open={authorsOverlayOpen}
          onOpen={() => setAuthorsOverlayOpen(true)}
          onClose={() => setAuthorsOverlayOpen(false)}
          renderAnchor={anchorProps => (
            <Button {...anchorProps} className={styles.peopleCount} size="small" variant="invisible">
              {commitAuthors.length} people
            </Button>
          )}
          focusZoneSettings={{disabled: true}}
          overlayProps={{role: 'dialog', 'aria-label': 'Commit authors'}}
        >
          <Stack padding="normal">
            <Text>{authorTooltip}</Text>
          </Stack>
        </AnchoredOverlay>
      )}
      {!committerAttribution ? (
        <span>committed&nbsp;</span>
      ) : (
        <>
          <span>authored and&nbsp;</span>
          <GitHubAvatar
            size={16}
            src={committer.development_avatar || committer.avatar_url}
            className={styles.committerAvatar}
          />
          {committerUrl ? (
            <Link href={`/${committerUrl}`}>
              <span className={styles.authorName}>{committer.login}&nbsp;</span>
            </Link>
          ) : (
            <span className={styles.authorName}>{committer.display_name}&nbsp;</span>
          )}
          <span>committed&nbsp;</span>
        </>
      )}
    </div>
  )
}
